// Facebook Import Web Component
import logger from '../utils/logger.js';
import { escapeHtml } from '../utils/html-utils.js';
import { openGoogleDrivePicker } from './google-drive-picker.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class FacebookImport extends HTMLElement {
  constructor() {
    super();
    this.jobs = [];
    this.isLoading = false;
    this.isStartingImport = false;
    this.errorMessage = null;
    this.successMessage = null;
    this.currentUser = null;
    this.pollingInterval = null;
    this.googleDriveAccounts = [];
    this.isLoadingAccounts = false;

    // Friends import state
    this.friendsFileId = null;
    this.friendsFileName = null;

    // Posts import state
    this.postsFileId = null;
    this.postsFileName = null;
    this.commentsFileId = null;
    this.commentsFileName = null;
    this.photoFileIds = [];
    this.photoFileNames = [];
    this.videoFileIds = [];
    this.videoFileNames = [];

    // Active tab: 'friends' or 'posts'
    this.activeTab = 'friends';
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.loadUserAndJobs();
  }

  disconnectedCallback() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  async loadUserAndJobs() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      // Get current user
      const authData = await window.API.request('/auth/status');
      if (!authData.authenticated) {
        throw new Error('User not authenticated');
      }

      this.currentUser = authData.user;
      await Promise.all([
        this.loadJobs(),
        this.loadGoogleDriveAccounts()
      ]);
    } catch (error) {
      logger.error('Failed to load user or jobs:', error);
      this.errorMessage = 'Failed to load import jobs';
      this.isLoading = false;
      this.render();
    }
  }

  async loadGoogleDriveAccounts() {
    try {
      if (!window.API || !this.currentUser) {
        return;
      }

      this.isLoadingAccounts = true;
      const data = await window.API.request(`/users/settings/${this.currentUser.id}/media-sources`);
      this.googleDriveAccounts = data.accounts?.['google-drive'] || [];
      this.isLoadingAccounts = false;
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to load Google Drive accounts:', error);
      this.isLoadingAccounts = false;
    }
  }

  async openFilePicker(fileType, multiselect = false) {
    if (this.googleDriveAccounts.length === 0) {
      this.errorMessage = 'Please connect a Google Drive account first in Sparkle Sources settings.';
      this.render();
      return;
    }

    const account = this.googleDriveAccounts[0];
    try {
      await openGoogleDrivePicker(this, 'google-drive', account.accountIdentifier, 'DOCS', (data) => {
        if (data.action === google.picker.Action.PICKED) {
          const docs = data.docs || [];

          if (multiselect) {
            // Handle multiple files
            if (fileType === 'photos') {
              this.photoFileIds = docs.map(doc => doc.id);
              this.photoFileNames = docs.map(doc => doc.name || 'Unknown');
            } else if (fileType === 'videos') {
              this.videoFileIds = docs.map(doc => doc.id);
              this.videoFileNames = docs.map(doc => doc.name || 'Unknown');
            }
          } else {
            // Handle single file
            const file = docs[0];
            if (file) {
              if (fileType === 'friends') {
                this.friendsFileId = file.id;
                this.friendsFileName = file.name || 'Selected File';
              } else if (fileType === 'posts') {
                this.postsFileId = file.id;
                this.postsFileName = file.name || 'Selected File';
              } else if (fileType === 'comments') {
                this.commentsFileId = file.id;
                this.commentsFileName = file.name || 'Selected File';
              }
            }
          }

          this.errorMessage = null;
          this.render();
          this.attachEventListeners();
        }
      }, multiselect);
    } catch (error) {
      logger.error('Failed to open file picker:', error);
      this.errorMessage = error.message || 'Failed to open Google Drive file picker. Please try again.';
      this.render();
    }
  }

  async loadJobs() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const data = await window.API.request('/facebook-import/jobs');
      this.jobs = data.jobs || [];
      this.isLoading = false;
      this.render();
    } catch (error) {
      logger.error('Failed to load import jobs:', error);
      this.errorMessage = 'Failed to load import jobs';
      this.isLoading = false;
      this.render();
    }
  }

  async startFriendsImport() {
    if (!this.friendsFileId) {
      this.errorMessage = 'Please select the friends JSON file';
      this.render();
      return;
    }

    this.isStartingImport = true;
    this.errorMessage = null;
    this.successMessage = null;
    this.render();

    try {
      const response = await window.API.request('/facebook-import/start', {
        method: 'POST',
        body: JSON.stringify({
          importType: 'friends_only',
          friendsFileId: this.friendsFileId
        })
      });

      this.successMessage = response.message || 'Friends import started successfully';
      this.friendsFileId = null;
      this.friendsFileName = null;
      await this.loadJobs();
      this.startPolling();
      this.isStartingImport = false;
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to start friends import:', error);
      this.errorMessage = error.message || 'Failed to start friends import. Please try again.';
      this.isStartingImport = false;
      this.render();
      this.attachEventListeners();
    }
  }

  async startPostsImport() {
    if (!this.postsFileId) {
      this.errorMessage = 'Please select the posts JSON file';
      this.render();
      return;
    }

    this.isStartingImport = true;
    this.errorMessage = null;
    this.successMessage = null;
    this.render();

    try {
      const response = await window.API.request('/facebook-import/start', {
        method: 'POST',
        body: JSON.stringify({
          importType: 'posts_only',
          postsFileId: this.postsFileId,
          commentsFileId: this.commentsFileId || null,
          photoFileIds: this.photoFileIds,
          videoFileIds: this.videoFileIds
        })
      });

      this.successMessage = response.message || 'Posts import started successfully';
      // Clear form
      this.postsFileId = null;
      this.postsFileName = null;
      this.commentsFileId = null;
      this.commentsFileName = null;
      this.photoFileIds = [];
      this.photoFileNames = [];
      this.videoFileIds = [];
      this.videoFileNames = [];

      await this.loadJobs();
      this.startPolling();
      this.isStartingImport = false;
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to start posts import:', error);
      this.errorMessage = error.message || 'Failed to start posts import. Please try again.';
      this.isStartingImport = false;
      this.render();
      this.attachEventListeners();
    }
  }

  startPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.pollingInterval = setInterval(async () => {
      const hasActiveJobs = this.jobs.some(job =>
        job.status === 'pending' || job.status === 'processing'
      );

      if (hasActiveJobs) {
        await this.loadJobs();
        this.render();
        this.attachEventListeners();
      } else {
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
        }
        this.render();
        this.attachEventListeners();
      }
    }, 5000);
  }

  hasActiveJob() {
    return this.jobs.some(job => job.status === 'pending' || job.status === 'processing');
  }

  switchTab(tab) {
    this.activeTab = tab;
    this.errorMessage = null;
    this.successMessage = null;
    this.render();
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Tab switching
    const friendsTab = this.querySelector('#friendsTab');
    const postsTab = this.querySelector('#postsTab');
    if (friendsTab) {
      friendsTab.addEventListener('click', () => this.switchTab('friends'));
    }
    if (postsTab) {
      postsTab.addEventListener('click', () => this.switchTab('posts'));
    }

    // Friends import
    const pickFriendsBtn = this.querySelector('#pickFriendsFileBtn');
    if (pickFriendsBtn) {
      pickFriendsBtn.addEventListener('click', () => this.openFilePicker('friends', false));
    }
    const startFriendsBtn = this.querySelector('#startFriendsImportBtn');
    if (startFriendsBtn) {
      startFriendsBtn.addEventListener('click', () => this.startFriendsImport());
    }

    // Posts import
    const pickPostsBtn = this.querySelector('#pickPostsFileBtn');
    if (pickPostsBtn) {
      pickPostsBtn.addEventListener('click', () => this.openFilePicker('posts', false));
    }
    const pickCommentsBtn = this.querySelector('#pickCommentsFileBtn');
    if (pickCommentsBtn) {
      pickCommentsBtn.addEventListener('click', () => this.openFilePicker('comments', false));
    }
    const pickPhotosBtn = this.querySelector('#pickPhotosBtn');
    if (pickPhotosBtn) {
      pickPhotosBtn.addEventListener('click', () => this.openFilePicker('photos', true));
    }
    const pickVideosBtn = this.querySelector('#pickVideosBtn');
    if (pickVideosBtn) {
      pickVideosBtn.addEventListener('click', () => this.openFilePicker('videos', true));
    }
    const startPostsBtn = this.querySelector('#startPostsImportBtn');
    if (startPostsBtn) {
      startPostsBtn.addEventListener('click', () => this.startPostsImport());
    }

    // Clear buttons
    this.querySelectorAll('.clear-file-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const fileType = e.target.dataset.fileType;
        if (fileType === 'friends') {
          this.friendsFileId = null;
          this.friendsFileName = null;
        } else if (fileType === 'posts') {
          this.postsFileId = null;
          this.postsFileName = null;
        } else if (fileType === 'comments') {
          this.commentsFileId = null;
          this.commentsFileName = null;
        } else if (fileType === 'photos') {
          this.photoFileIds = [];
          this.photoFileNames = [];
        } else if (fileType === 'videos') {
          this.videoFileIds = [];
          this.videoFileNames = [];
        }
        this.render();
        this.attachEventListeners();
      });
    });

    // Delete job buttons
    this.querySelectorAll('.delete-job-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const jobId = e.target.dataset.jobId;
        if (jobId && confirm('Are you sure you want to delete this import job?')) {
          try {
            await window.API.request(`/facebook-import/jobs/${jobId}`, {
              method: 'DELETE'
            });
            await this.loadJobs();
            this.render();
            this.attachEventListeners();
          } catch (error) {
            logger.error('Failed to delete job:', error);
            this.errorMessage = error.message || 'Failed to delete job';
            this.render();
          }
        }
      });
    });

    // Return to settings
    const returnBtn = this.querySelector('#returnToSettingsBtn');
    if (returnBtn) {
      returnBtn.addEventListener('click', () => {
        window.router.navigate('/settings');
      });
    }
  }

  render() {
    const hasActiveJobs = this.hasActiveJob();
    const hasConnectedGoogleDrive = this.googleDriveAccounts.length > 0;

    this.innerHTML = `
      <div class="facebook-import">
        <div class="settings-header">
          ${renderPageTitle('📥', 'Facebook Import')}
          <p>Import your Facebook posts and friends from Google Drive files</p>
        </div>

        ${this.errorMessage ? `<div class="alert alert-error">${escapeHtml(this.errorMessage)}</div>` : ''}
        ${this.successMessage ? `<div class="alert alert-success">${escapeHtml(this.successMessage)}</div>` : ''}

        ${!hasConnectedGoogleDrive ? `
          <div class="help-warning">
            ⚠️ You need to connect a Google Drive account first (if you haven't already).
            <a href="/settings/sparkle-sources">Go to Sparkle Sources settings</a> to connect.
          </div>
        ` : ''}

        <div class="import-tabs">
          <button class="tab-btn ${this.activeTab === 'friends' ? 'active' : ''}" id="friendsTab">
            👥 Import Friends
          </button>
          <button class="tab-btn ${this.activeTab === 'posts' ? 'active' : ''}" id="postsTab">
            📝 Import Posts
          </button>
        </div>

        ${this.activeTab === 'friends' ? `
          <div class="import-section">
            <h3>Import Friends</h3>
            <p class="section-description">Select your <code>your_friends.json</code> file from your Facebook export.</p>
            
            <div class="form-group">
              <label>Friends JSON File</label>
              ${this.friendsFileId ? `
                <div class="selected-file">
                  <span class="file-icon">📄</span>
                  <span class="file-name">${escapeHtml(this.friendsFileName || 'Selected File')}</span>
                  <button type="button" class="btn-clear-file clear-file-btn" data-file-type="friends">Change</button>
                </div>
              ` : `
                <button
                  type="button"
                  id="pickFriendsFileBtn"
                  class="btn btn-secondary"
                  ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
                >
                  ${this.isLoadingAccounts ? 'Loading...' : '📁 Pick Friends JSON File'}
                </button>
              `}
            </div>

            <button 
              type="button"
              id="startFriendsImportBtn"
              class="btn btn-primary"
              ${this.isStartingImport || hasActiveJobs || !this.friendsFileId ? 'disabled' : ''}
            >
              ${this.isStartingImport ? 'Starting Import...' : 'Start Friends Import'}
            </button>
          </div>
        ` : `
          <div class="import-section">
            <h3>Import Posts</h3>
            <p class="section-description">Select your Facebook posts data files. The posts JSON file is required; comments, photos, and videos are optional.</p>
            
            <div class="form-group">
              <label>Posts JSON File <span class="required">*</span></label>
              ${this.postsFileId ? `
                <div class="selected-file">
                  <span class="file-icon">📄</span>
                  <span class="file-name">${escapeHtml(this.postsFileName || 'Selected File')}</span>
                  <button type="button" class="btn-clear-file clear-file-btn" data-file-type="posts">Change</button>
                </div>
              ` : `
                <button
                  type="button"
                  id="pickPostsFileBtn"
                  class="btn btn-secondary"
                  ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
                >
                  ${this.isLoadingAccounts ? 'Loading...' : '📁 Pick Posts JSON File'}
                </button>
              `}
            </div>

            <div class="form-group">
              <label>Comments JSON File <span class="optional">(optional)</span></label>
              ${this.commentsFileId ? `
                <div class="selected-file">
                  <span class="file-icon">📄</span>
                  <span class="file-name">${escapeHtml(this.commentsFileName || 'Selected File')}</span>
                  <button type="button" class="btn-clear-file clear-file-btn" data-file-type="comments">Change</button>
                </div>
              ` : `
                <button
                  type="button"
                  id="pickCommentsFileBtn"
                  class="btn btn-secondary"
                  ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
                >
                  ${this.isLoadingAccounts ? 'Loading...' : '📁 Pick Comments JSON File'}
                </button>
              `}
            </div>

            <div class="form-group">
              <label>Photos <span class="optional">(optional, multiselect)</span></label>
              ${this.photoFileIds.length > 0 ? `
                <div class="selected-files">
                  <div class="files-list">
                    ${this.photoFileNames.map((name, _idx) => `
                      <div class="file-item">
                        <span class="file-icon">🖼️</span>
                        <span class="file-name">${escapeHtml(name)}</span>
                      </div>
                    `).join('')}
                  </div>
                  <button type="button" class="btn-clear-file clear-file-btn" data-file-type="photos">Change</button>
                </div>
              ` : `
                <button
                  type="button"
                  id="pickPhotosBtn"
                  class="btn btn-secondary"
                  ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
                >
                  ${this.isLoadingAccounts ? 'Loading...' : '🖼️ Pick Photos (Select Multiple)'}
                </button>
              `}
            </div>

            <div class="form-group">
              <label>Videos <span class="optional">(optional, multiselect)</span></label>
              ${this.videoFileIds.length > 0 ? `
                <div class="selected-files">
                  <div class="files-list">
                    ${this.videoFileNames.map((name, _idx) => `
                      <div class="file-item">
                        <span class="file-icon">🎥</span>
                        <span class="file-name">${escapeHtml(name)}</span>
                      </div>
                    `).join('')}
                  </div>
                  <button type="button" class="btn-clear-file clear-file-btn" data-file-type="videos">Change</button>
                </div>
              ` : `
                <button
                  type="button"
                  id="pickVideosBtn"
                  class="btn btn-secondary"
                  ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
                >
                  ${this.isLoadingAccounts ? 'Loading...' : '🎥 Pick Videos (Select Multiple)'}
                </button>
              `}
            </div>

            <button 
              type="button"
              id="startPostsImportBtn"
              class="btn btn-primary"
              ${this.isStartingImport || hasActiveJobs || !this.postsFileId ? 'disabled' : ''}
            >
              ${this.isStartingImport ? 'Starting Import...' : 'Start Posts Import'}
            </button>
          </div>
        `}

        ${hasActiveJobs ? '<p class="active-job-message">An import job is already in progress. Please wait or delete the active job.</p>' : ''}

        <div class="import-jobs-section">
          <h3>Your Import Jobs</h3>
          ${this.isLoading && !hasActiveJobs ? `
            <div class="loading-state">
              <div class="spinner"></div>
              <div>Loading import jobs...</div>
            </div>
          ` : this.jobs.length === 0 ? `
            <p class="empty-state">No import jobs found. Start one above!</p>
          ` : `
            <ul class="job-list">
              ${this.jobs.map(job => `
                <li class="job-item">
                  <div class="job-details">
                    <span class="job-id">#${job.id}</span>
                    <span class="job-status status-${job.status}">${escapeHtml(job.status)}</span>
                    <span class="job-created-at">${new Date(job.createdAt).toLocaleString()}</span>
                  </div>
                  <div class="job-progress">
                    <span class="progress-text">Posts: ${job.postsImported} | Friends: ${job.friendsFound}</span>
                    ${job.status === 'processing' ? '<span class="spinner spinner-small"></span>' : ''}
                  </div>
                  ${job.errorMessage ? `<div class="job-error">Error: ${escapeHtml(job.errorMessage)}</div>` : ''}
                  <div class="job-actions">
                    <button class="btn btn-sm btn-danger delete-job-btn" data-job-id="${job.id}"
                            ${job.status === 'processing' ? 'disabled' : ''}>
                      Delete
                    </button>
                  </div>
                </li>
              `).join('')}
            </ul>
          `}
        </div>

        <div class="settings-footer">
          <button class="btn btn-secondary" id="returnToSettingsBtn">
            ← Return to Settings
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('facebook-import', FacebookImport);
